import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
    BarChart3, Download, PieChart, 
    TrendingUp, Calendar, Filter,
    FileText, Loader2, RefreshCcw
} from 'lucide-react';

const API_BASE = (import.meta.env.VITE_APP_API_URL && import.meta.env.VITE_APP_API_URL !== "undefined") ? import.meta.env.VITE_APP_API_URL : "https://apiessentialcarehomehealth.saasbizhub.com/api/v1";

export default function CrmReports() {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const fetchReportData = async () => {
        const token = localStorage.getItem('admin_token');
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/admin/crm/reports/aggregation`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReportData(response.data);
        } catch (err) {
            toast.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReportData();
    }, []);

    const handleExportLeads = async () => {
        const token = localStorage.getItem('admin_token');
        try {
            setIsExporting(true);
            const response = await axios.get(`${API_BASE}/admin/crm/reports/export/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `leads_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Leads exported successfully');
        } catch (err) {
            toast.error('Failed to export leads');
        } finally {
            setIsExporting(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-12 h-12 text-teal-600 animate-spin" />
        </div>
    );

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <Toaster position="top-right" />
            
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600">
                        CRM Analytics & Reports
                    </h1>
                    <p className="text-gray-500 mt-1">Detailed breakdown of lead performance and sources.</p>
                </div>
                <div className="flex gap-3">
                    <button 
                        onClick={fetchReportData}
                        className="p-2.5 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-xl transition-all"
                        title="Refresh Data"
                    >
                        <RefreshCcw className="w-5 h-5" />
                    </button>
                    <button 
                        onClick={handleExportLeads}
                        disabled={isExporting}
                        className="inline-flex items-center px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-70"
                    >
                        {isExporting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />}
                        Export Leads (CSV)
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Status Distribution */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-teal-600" />
                        Lead Status Distribution
                    </h3>
                    <div className="space-y-6">
                        {reportData?.by_status?.map(item => {
                            const total = reportData.by_status.reduce((acc, curr) => acc + curr.total, 0);
                            const percent = Math.round((item.total / total) * 100);
                            return (
                                <div key={item.status} className="space-y-2">
                                    <div className="flex justify-between text-sm font-medium">
                                        <span className="text-gray-600">{item.status}</span>
                                        <span className="text-gray-900">{item.total} ({percent}%)</span>
                                    </div>
                                    <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                        <motion.div 
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percent}%` }}
                                            transition={{ duration: 1, ease: 'easeOut' }}
                                            className="h-full bg-teal-500"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Top Sources */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-teal-600" />
                        Top Referral Sources
                    </h3>
                    <div className="space-y-6">
                        {reportData?.by_source?.map(item => (
                            <div key={item.facility_name} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                <div>
                                    <p className="font-bold text-gray-900">{item.facility_name || 'Direct / Unknown'}</p>
                                    <p className="text-xs text-gray-400 uppercase tracking-tighter">Referral Channel</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-black text-teal-600">{item.total}</p>
                                    <p className="text-[10px] text-gray-400">Total Leads</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
